import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Wallet, IndianRupee, AlertTriangle, CalendarClock, TrendingUp, Scale,
  Search, Plus, Phone, MessageCircle, X, Loader2, ArrowRight, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["All", "Pending", "Partially Paid", "Paid", "Overdue"];
const STATUS_BADGE = {
  Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Partially Paid": "bg-blue-100 text-blue-800 border-blue-200",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Overdue: "bg-red-100 text-red-800 border-red-200",
};

function inr(n) {
  return `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}
    >
      {status}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, accent, testid, sub }) {
  return (
    <Card className="p-4 sm:p-6 border-border hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="overline text-zinc-500">{label}</div>
          <div className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mt-1 num truncate">{value}</div>
          {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md grid place-items-center ${accent} flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.7} />
        </div>
      </div>
    </Card>
  );
}

function CreatePaymentDialog({ open, onOpenChange, onCreated, customers, orders }) {
  const [form, setForm] = useState({
    customer_id: "",
    mobile: "",
    invoice_number: "",
    order_id: "",
    order_number: "",
    product_name: "",
    gold_weight: 0,
    total_amount: 0,
    amount_paid: 0,
    due_date: "",
    reminder_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find((x) => x.id === form.customer_id);
      if (c) {
        setForm((f) => ({ ...f, mobile: c.primary_phone || f.mobile }));
      }
    }
    // eslint-disable-next-line
  }, [form.customer_id]);

  useEffect(() => {
    if (form.order_id) {
      const o = orders.find((x) => x.id === form.order_id);
      if (o) {
        setForm((f) => ({
          ...f,
          order_number: o.order_no,
          product_name: f.product_name || o.title,
          gold_weight: f.gold_weight || o.final_weight || o.estimated_weight || 0,
        }));
      }
    }
    // eslint-disable-next-line
  }, [form.order_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select a customer");
    if (!form.product_name) return toast.error("Product name required");
    if (!form.total_amount || form.total_amount <= 0) return toast.error("Total amount required");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        order_id: form.order_id || null,
        gold_weight: parseFloat(form.gold_weight) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        reminder_date: form.reminder_date ? new Date(form.reminder_date).toISOString() : null,
      };
      const { data } = await api.post("/payments", payload);
      toast.success("Payment reminder created");
      onCreated(data);
      onOpenChange(false);
      setForm({
        customer_id: "", mobile: "", invoice_number: "", order_id: "", order_number: "",
        product_name: "", gold_weight: 0, total_amount: 0, amount_paid: 0,
        due_date: "", reminder_date: "", notes: "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Payment Reminder</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="payment-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.primary_phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="auto-filled from customer" data-testid="payment-mobile" />
            </div>
            <div className="space-y-2">
              <Label>Invoice Number</Label>
              <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="payment-invoice" />
            </div>
            <div className="space-y-2">
              <Label>Link to Order (optional)</Label>
              <Select value={form.order_id || "none"} onValueChange={(v) => setForm({ ...form, order_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {orders.filter((o) => !form.customer_id || o.customer_id === form.customer_id).map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.order_no} · {o.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Number (manual)</Label>
              <Input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Product Name *</Label>
              <Input required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="payment-product" />
            </div>
            <div className="space-y-2">
              <Label>Gold Weight (g)</Label>
              <Input type="number" step="0.001" value={form.gold_weight} onChange={(e) => setForm({ ...form, gold_weight: e.target.value })} data-testid="payment-weight" />
            </div>
            <div className="space-y-2">
              <Label>Total Amount ₹ *</Label>
              <Input type="number" step="0.01" required value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="payment-amount" />
            </div>
            <div className="space-y-2">
              <Label>Amount Paid ₹ (upfront)</Label>
              <Input type="number" step="0.01" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="payment-due" />
            </div>
            <div className="space-y-2">
              <Label>Reminder Date</Label>
              <Input type="date" value={form.reminder_date} onChange={(e) => setForm({ ...form, reminder_date: e.target.value })} data-testid="payment-reminder" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800" data-testid="payment-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Reminder
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentRow({ p }) {
  const digits = (p.mobile || "").replace(/[^0-9]/g, "");
  return (
    <>
      {/* Mobile card */}
      <div className="md:hidden p-4">
        <Link to={`/payments/${p.id}`} className="block min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={p.status} />
            {p.invoice_number && <span className="font-mono text-[10px] text-zinc-500">INV {p.invoice_number}</span>}
            {p.order_number && <span className="font-mono text-[10px] text-zinc-500">{p.order_number}</span>}
          </div>
          <div className="font-medium text-sm mt-1 truncate">{p.product_name}</div>
          <div className="text-xs text-zinc-500 truncate">
            {p.customer_name || "—"}{p.mobile ? ` · ${p.mobile}` : ""}
            {p.gold_weight > 0 && <> · {p.gold_weight}g</>}
          </div>
        </Link>
        <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="overline text-zinc-500">Total</div>
            <div className="num font-semibold">{inr(p.total_amount)}</div>
          </div>
          <div>
            <div className="overline text-zinc-500">Balance</div>
            <div className="num font-semibold text-red-700">{inr(p.remaining_balance)}</div>
          </div>
          <div>
            <div className="overline text-zinc-500">Due</div>
            <div className="text-zinc-800">{p.due_date ? new Date(p.due_date).toLocaleDateString() : "—"}</div>
          </div>
        </div>
        {p.mobile && (
          <div className="mt-3 flex gap-3">
            <Button size="sm" variant="outline" className="flex-1 h-10 gap-2"
              onClick={(e) => { e.preventDefault(); window.location.href = `tel:${p.mobile}`; }}>
              <Phone className="w-4 h-4" /> Call
            </Button>
            <Button size="sm" variant="outline" className="flex-1 h-10 gap-2 text-emerald-600"
              onClick={(e) => { e.preventDefault(); window.open(`https://wa.me/${digits}`, "_blank"); }}>
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </Button>
          </div>
        )}
      </div>

      {/* Desktop row */}
      <div className="hidden md:grid md:grid-cols-[1fr_140px_140px_120px_100px] gap-3 p-4 hover:bg-zinc-50 items-center">
        <Link to={`/payments/${p.id}`} className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={p.status} />
            {p.invoice_number && <span className="font-mono text-[10px] text-zinc-500">INV {p.invoice_number}</span>}
            {p.order_number && <span className="font-mono text-[10px] text-zinc-500">{p.order_number}</span>}
          </div>
          <div className="font-medium text-sm mt-1 truncate">{p.product_name}</div>
          <div className="text-xs text-zinc-500 truncate">
            {p.customer_name || "—"}{p.mobile ? ` · ${p.mobile}` : ""}
            {p.gold_weight > 0 && <> · {p.gold_weight}g</>}
          </div>
        </Link>
        <div className="text-right">
          <div className="num text-sm font-medium">{inr(p.total_amount)}</div>
        </div>
        <div className="text-right">
          <div className="num text-sm font-semibold text-red-700">{inr(p.remaining_balance)}</div>
        </div>
        <div className="text-right text-sm text-zinc-600">
          {p.due_date ? new Date(p.due_date).toLocaleDateString() : "—"}
        </div>
        <div className="flex items-center gap-1 justify-end">
          {p.mobile && (
            <>
              <Button size="icon" variant="outline" className="h-8 w-8"
                onClick={(e) => { e.preventDefault(); window.location.href = `tel:${p.mobile}`; }}>
                <Phone className="w-3.5 h-3.5" />
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600"
                onClick={(e) => { e.preventDefault(); window.open(`https://wa.me/${digits}`, "_blank"); }}>
                <MessageCircle className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function Payments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);

  const status = searchParams.get("status") || "All";
  const q = searchParams.get("q") || "";

  const setParam = (k, v) => {
    const n = new URLSearchParams(searchParams);
    if (!v || v === "All") n.delete(k);
    else n.set(k, v);
    setSearchParams(n, { replace: true });
  };

  const loadDash = useCallback(async () => {
    const { data } = await api.get("/payments/dashboard");
    setDash(data);
  }, []);
  const loadList = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "All") params.status = status;
    const { data } = await api.get("/payments", { params });
    setItems(data);
  }, [q, status]);

  useEffect(() => {
    loadDash();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, [loadDash]);
  useEffect(() => { loadList(); }, [loadList]);

  const maxG = dash ? Math.max(1, ...dash.graph_30d.map((d) => d.amount)) : 1;

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500">Receivables</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Payment Reminders</h1>
          <p className="text-sm text-zinc-500 mt-2">Track outstanding balances, follow-ups, and collections.</p>
        </div>
        <Button className="bg-zinc-900 hover:bg-zinc-800 h-10" onClick={() => setOpen(true)} data-testid="new-payment-button">
          <Plus className="w-4 h-4 mr-1.5" /> New Payment
        </Button>
      </div>

      {/* KPIs */}
      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard label="Total Outstanding" value={inr(dash.kpis.total_outstanding)}
            sub={dash.kpis.opening_receivable > 0 ? `incl. opening ${inr(dash.kpis.opening_receivable)}` : ""}
            icon={IndianRupee} accent="bg-zinc-900 text-white" testid="kpi-outstanding" />
          <KpiCard label="Total Paid" value={inr(dash.kpis.total_paid)}
            icon={TrendingUp} accent="bg-emerald-100 text-emerald-800" testid="kpi-paid" />
          <KpiCard label="Overdue" value={dash.kpis.overdue_count}
            sub={dash.kpis.overdue_amount > 0 ? inr(dash.kpis.overdue_amount) + " pending" : ""}
            icon={AlertTriangle} accent="bg-red-100 text-red-800" testid="kpi-overdue" />
          <KpiCard label="Due Today" value={dash.kpis.due_today_count}
            sub={`${dash.kpis.due_tomorrow_count} due tomorrow`}
            icon={CalendarClock} accent="bg-yellow-100 text-yellow-800" testid="kpi-due-today" />
          <KpiCard label="This Month Collection" value={inr(dash.kpis.this_month_collection)}
            icon={Wallet} accent="bg-blue-100 text-blue-800" testid="kpi-month-collection" />
          <KpiCard label="Pending Gold" value={`${(dash.kpis.pending_gold_weight || 0).toFixed(3)} g`}
            icon={Scale} accent="bg-amber-100 text-amber-800" testid="kpi-pending-gold" />
          <KpiCard label="Pending Silver" value={`${(dash.kpis.pending_silver_weight || 0).toFixed(3)} g`}
            icon={Scale} accent="bg-slate-200 text-slate-800" testid="kpi-pending-silver" />
          <KpiCard label="Total Records" value={dash.kpis.total_records}
            sub={`${dash.kpis.due_tomorrow_count} due tomorrow`}
            icon={Receipt} accent="bg-zinc-100 text-zinc-800" testid="kpi-records" />
        </div>
      )}

      {/* Graph */}
      {dash && (
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          <Card className="p-4 sm:p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Collection — Last 30 days
            </h3>
            <div className="flex items-end gap-1 h-32 sm:h-40" data-testid="collection-graph">
              {dash.graph_30d.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative min-w-0">
                  <div
                    className="w-full bg-zinc-900 rounded-t-sm transition-all hover:bg-gold-500"
                    style={{ height: `${(d.amount / maxG) * 100}%`, minHeight: d.amount > 0 ? "3px" : "0" }}
                    title={`${d.date}: ${inr(d.amount)}`}
                  />
                  {/* Removed the per-bar date label to prevent crowding 30 items */}
                </div>
              ))}
            </div>
            <div className="text-xs text-zinc-500 mt-2 hidden sm:flex justify-between">
              <span>{dash.graph_30d[0]?.date}</span>
              <span>{dash.graph_30d[dash.graph_30d.length - 1]?.date}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-2 flex justify-between sm:hidden">
              <span>{dash.graph_30d[0]?.date}</span>
              <span>{dash.graph_30d[dash.graph_30d.length - 1]?.date}</span>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Monthly Collection
            </h3>
            <div className="flex items-end gap-1 sm:gap-2 h-32 sm:h-40" data-testid="monthly-chart">
              {dash.monthly_chart.map((m) => {
                const maxM = Math.max(1, ...dash.monthly_chart.map((x) => x.amount));
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end group min-w-0">
                    <div
                      className="w-full bg-zinc-900 rounded-t-sm hover:bg-gold-500 transition-all"
                      style={{ height: `${(m.amount / maxM) * 100}%`, minHeight: m.amount > 0 ? "3px" : "0" }}
                      title={`${m.label}: ${inr(m.amount)}`}
                    />
                    <div className="text-[8px] sm:text-[10px] text-zinc-500 mt-1 truncate w-full text-center">
                      {m.label.split(" ")[0]} {/* Only show 'Aug', 'Sep', etc. to save space */}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Top Pending / Overdue / Upcoming Reminders */}
      {dash && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="p-4 sm:p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <IndianRupee className="w-4 h-4" /> Top Pending Customers
            </h3>
            {dash.top_pending_customers.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No pending customers.</div>
            ) : (
              <ul className="divide-y divide-border" data-testid="top-pending-list">
                {dash.top_pending_customers.map((c) => (
                  <li key={c.customer_id} className="py-2.5 flex items-center justify-between gap-2">
                    <Link to={`/customers/${c.customer_id}`} className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{c.customer_name}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {c.records ? `${c.records} record(s)` : ""}
                        {c.opening_balance ? " · Opening balance" : ""}
                        {c.gold > 0 && ` · ${c.gold.toFixed(3)}g Au`}
                        {c.silver > 0 && ` · ${c.silver.toFixed(3)}g Ag`}
                      </div>
                    </Link>
                    <div className="text-sm num font-semibold text-right whitespace-nowrap">{inr(c.amount)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" /> Overdue Payments
            </h3>
            {dash.overdue_list.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No overdue payments.</div>
            ) : (
              <ul className="divide-y divide-border" data-testid="overdue-list">
                {dash.overdue_list.slice(0, 6).map((p) => (
                  <li key={p.id} className="py-2.5">
                    <Link to={`/payments/${p.id}`} className="block hover:bg-zinc-50 -mx-2 px-2 rounded">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium truncate">{p.customer_name}</div>
                        <div className="text-sm num font-semibold whitespace-nowrap">{inr(p.remaining_balance)}</div>
                      </div>
                      <div className="text-xs text-zinc-500">
                        {p.product_name} · Due {new Date(p.due_date).toLocaleDateString()}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <CalendarClock className="w-4 h-4" /> Upcoming Reminders
            </h3>
            {dash.upcoming_reminders.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No reminders in the next 7 days.</div>
            ) : (
              <ul className="divide-y divide-border" data-testid="upcoming-reminders-list">
                {dash.upcoming_reminders.map((p) => (
                  <li key={p.id} className="py-2.5">
                    <Link to={`/payments/${p.id}`} className="block hover:bg-zinc-50 -mx-2 px-2 rounded">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate">{p.customer_name}</div>
                        <StatusPill status={p.status} />
                      </div>
                      <div className="text-xs text-zinc-500">
                        {p.product_name} · {new Date(p.reminder_date || p.due_date).toLocaleDateString()}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      <Tabs defaultValue="list">
        <TabsList className="flex w-full flex-wrap h-auto justify-start gap-1" data-testid="payment-tabs">
          <TabsTrigger value="list" data-testid="payments-tab-list">All Reminders</TabsTrigger>
          <TabsTrigger value="today" data-testid="payments-tab-today">
            Due Today {dash && dash.due_today.length > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 rounded-full">{dash.due_today.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="tomorrow" data-testid="payments-tab-tomorrow">Due Tomorrow</TabsTrigger>
          <TabsTrigger value="recent" data-testid="payments-tab-recent">Recent Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="pt-4 space-y-3">
          <Card className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <Input placeholder="Search invoice, product, mobile…" value={q}
                  onChange={(e) => setParam("q", e.target.value)}
                  className="pl-9" data-testid="payment-search" />
              </div>
              <Select value={status} onValueChange={(v) => setParam("status", v)}>
                <SelectTrigger data-testid="payment-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-zinc-500 mt-3 num">{items.length} record(s)</div>
          </Card>
          <Card className="p-0 overflow-hidden">
            {items.length === 0 ? (
              <div className="p-12 text-center text-sm text-zinc-500">
                <Receipt className="w-8 h-8 mx-auto mb-3 opacity-40" />
                No payment reminders yet. Click <b>New Payment</b> to add one.
              </div>
            ) : (
              <div className="divide-y divide-border" data-testid="payments-list">
                {/* Table header - desktop only */}
                <div className="hidden md:grid md:grid-cols-[1fr_140px_140px_120px_100px] items-center gap-3 p-3 bg-zinc-50 text-xs text-zinc-500 overline font-semibold">
                  <div>Customer & Product</div>
                  <div className="text-right">Total Amount</div>
                  <div className="text-right">Balance</div>
                  <div className="text-right">Due Date</div>
                  <div className="text-right">Actions</div>
                </div>
                {items.map((p) => <div key={p.id}><PaymentRow p={p} /></div>)}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="today" className="pt-4">
          <Card className="p-0 overflow-hidden">
            {!dash || dash.due_today.length === 0 ? (
              <div className="p-12 text-center text-sm text-zinc-500">Nothing due today.</div>
            ) : (
              <div className="divide-y divide-border">
                {/* Table header - desktop only */}
                <div className="hidden md:grid md:grid-cols-[1fr_140px_140px_120px_100px] items-center gap-3 p-3 bg-zinc-50 text-xs text-zinc-500 overline font-semibold">
                  <div>Customer & Product</div>
                  <div className="text-right">Total Amount</div>
                  <div className="text-right">Balance</div>
                  <div className="text-right">Due Date</div>
                  <div className="text-right">Actions</div>
                </div>
                {dash.due_today.map((p) => <div key={p.id}><PaymentRow p={p} /></div>)}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="tomorrow" className="pt-4">
          <Card className="p-0 overflow-hidden">
            {!dash || dash.due_tomorrow.length === 0 ? (
              <div className="p-12 text-center text-sm text-zinc-500">Nothing due tomorrow.</div>
            ) : (
              <div className="divide-y divide-border">
                {/* Table header - desktop only */}
                <div className="hidden md:grid md:grid-cols-[1fr_140px_140px_120px_100px] items-center gap-3 p-3 bg-zinc-50 text-xs text-zinc-500 overline font-semibold">
                  <div>Customer & Product</div>
                  <div className="text-right">Total Amount</div>
                  <div className="text-right">Balance</div>
                  <div className="text-right">Due Date</div>
                  <div className="text-right">Actions</div>
                </div>
                {dash.due_tomorrow.map((p) => <div key={p.id}><PaymentRow p={p} /></div>)}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="recent" className="pt-4">
          <Card className="p-0 overflow-hidden">
            {!dash || dash.recent_payments.length === 0 ? (
              <div className="p-12 text-center text-sm text-zinc-500">No collections yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {dash.recent_payments.map((r, i) => (
                  <li key={i} className="p-4 flex items-center justify-between gap-3">
                    <Link to={`/payments/${r.payment_id}`} className="min-w-0">
                      <div className="font-medium text-sm">{r.product_name}</div>
                      <div className="text-xs text-zinc-500">
                        {r.customer_name} · {new Date(r.date).toLocaleString()}
                      </div>
                    </Link>
                    <div className="text-right whitespace-nowrap">
                      <div className="num font-semibold">{inr(r.amount)}</div>
                      <div className="text-xs text-zinc-500">{r.mode}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <CreatePaymentDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => { loadDash(); loadList(); }}
        customers={customers}
        orders={orders}
      />
    </div>
  );
}
